import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"
import LegalDocumentForm, {
  type CustomerOption,
  type FormValues,
  type Option,
  type ParentOption,
} from "./components/LegalDocumentForm"

interface LegalDocument extends Record<string, unknown> {
  id: number
  customer_id: number | null
  parent_document_id: number | null
  document_type: string
  status: string
  title: string
  effective_date: string | null
  execution_date: string | null
  end_date: string | null
  governing_law: string | null
  currency: string | null
  document_url: string | null
  notes: string | null
}

interface Props {
  [key: string]: unknown
  document: LegalDocument
  customers: CustomerOption[]
  document_types: Option[]
  statuses: Option[]
  currencies: string[]
  parent_options: ParentOption[]
}

export default function LegalDocumentEdit() {
  const { document, customers, document_types, statuses, currencies, parent_options } = usePage<Props>().props

  const { data, setData, patch, processing, errors } = useForm<FormValues>({
    customer_id: document.customer_id ? String(document.customer_id) : "",
    parent_document_id: document.parent_document_id ? String(document.parent_document_id) : "",
    document_type: document.document_type,
    status: document.status,
    title: document.title,
    effective_date: document.effective_date || "",
    execution_date: document.execution_date || "",
    end_date: document.end_date || "",
    governing_law: document.governing_law || "",
    currency: document.currency || "",
    document_url: document.document_url || "",
    notes: document.notes || "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/legal_documents/${document.id}`)
  }

  const cancelHref = `/legal_documents/${document.id}`

  return (
    <>
      <Head title={`Edit ${document.title} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/legal_documents">Documents</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={cancelHref}>{document.title}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={cancelHref}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Document</h1>
          </div>
        </div>

        <div className="max-w-3xl">
          <LegalDocumentForm
            values={data}
            errors={errors}
            customers={customers}
            documentTypes={document_types}
            statuses={statuses}
            currencies={currencies}
            parentOptions={parent_options}
            processing={processing}
            submitLabel="Save Changes"
            cancelHref={cancelHref}
            onChange={(field, value) => setData(field, value)}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </>
  )
}

LegalDocumentEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
